/**
 * Persona lens copy book.
 *
 * Every scene × persona cell (10 × 4 = 40, each bilingual) is grounded in what
 * is literally painted on that reference PNG: Kirill's red signal and the
 * Newton's-laws gap, the seven-link chain, the five goals and four route axes,
 * the algebra graph around quadratic equations, Alexey's seven sliders and
 * Maria's route, the eight-step cycle, the mastery bars, Ms. Rivera's 7B
 * Science workspace with Avery J., the product-vs-research split, and the
 * teacher–system–learner loop. `test/studyninja-living-content.test.mjs`
 * asserts completeness, length limits, banned stems and that every
 * `tryThis.hotspot` exists in `hotspotsFor()` for that scene.
 *
 * Voice per persona:
 *   teacher   what to decide now, in classroom language; no jargon.
 *   expert    methodologist / learning scientist: mechanism, evidence,
 *             boundaries — what is product today vs research.
 *   parent    what the child experiences and feels; safety and control;
 *             no acronyms; never "your child is behind".
 *   investor  differentiation and what is real in the product today vs
 *             prototype; never invented metrics or promises.
 *
 * Limits: headline ≤ 64, insight ≤ 180, tryThis.label ≤ 64, questions ≤ 90.
 * EN copy is a native rewrite (no decorative dashes); RU keeps its dashes.
 */

import type { LensBook, Persona, PersonaMeta } from './types';

export const PERSONA_META: readonly PersonaMeta[] = [
  {
    id: 'teacher',
    label: { ru: 'Учитель', en: 'Teacher' },
    focus: { ru: 'Что решить на этом уроке', en: 'What to decide in this lesson' },
  },
  {
    id: 'expert',
    label: { ru: 'Эксперт', en: 'Expert' },
    focus: { ru: 'Механика и границы метода', en: 'Mechanism and its limits' },
  },
  {
    id: 'parent',
    label: { ru: 'Родитель', en: 'Parent' },
    focus: { ru: 'Что почувствует ребёнок', en: 'What the child will feel' },
  },
  {
    id: 'investor',
    label: { ru: 'Инвестор', en: 'Investor' },
    focus: { ru: 'Что здесь работает уже сегодня', en: 'What works in the product today' },
  },
];

/**
 * Per-persona addendum appended to the assistant system prompt by
 * /api/studyninja/ask (validated `viewerRole`). English on purpose: the base
 * prompt already fixes the answer language.
 */
export const PERSONA_PROMPT: Record<Persona, string> = {
  teacher:
    'The visitor is a practising teacher. Answer in plain classroom language, lead with what they could decide or do in the next lesson, and tie every point to something visible on the current scene. Skip theory and internal terminology unless they ask for it.',
  expert:
    'The visitor is a methodologist or learning scientist. Explain the mechanism behind what the scene shows, name the evidence the system relies on, and state plainly where the product ends and the research prototypes (MagicBrain, UWT, described by purpose only) begin. Prefer precise, falsifiable phrasing over enthusiasm.',
  parent:
    'The visitor is a parent. Describe what a child would experience and feel on this screen, what stays under the family’s and teacher’s control, and how the demonstration uses only synthetic learners. Avoid acronyms and technical terms, never describe a child as behind or failing, and keep the tone calm and concrete.',
  investor:
    'The visitor is an investor or partner evaluating the product. Focus on differentiation, the design decisions that are hard to copy, and a clear line between what works in the product today and what is a research prototype. Never invent metrics, traction figures, integrations, study results or forward-looking promises; if a number is not on the screen, say that it is not shown.',
};

export const LENS_BOOK: LensBook = {
  classroom: {
    teacher: {
      headline: { ru: 'Кому нужна помощь прямо сейчас', en: 'Who needs help right now' },
      insight: {
        ru: 'Двенадцать учеников — двенадцать состояний, а не одна средняя оценка. Красный сигнал у Кирилла ведёт к причине и к одному конкретному действию на урок.',
        en: 'Twelve learners are twelve states, not one average grade. Kirill’s red signal leads to a cause and to one concrete action for this lesson.',
      },
      tryThis: { hotspot: 'learner-8', label: { ru: 'Нажмите на Кирилла — откроется причина', en: 'Press Kirill to see the cause' } },
      questions: [
        { ru: 'Что мне сделать с Кириллом на этом уроке?', en: 'What should I do with Kirill in this lesson?' },
        { ru: 'Почему у Богдана жёлтый сигнал?', en: 'Why does Bogdan have an amber signal?' },
      ],
    },
    expert: {
      headline: { ru: 'Сигнал — это гипотеза о причине, а не оценка', en: 'A signal is a hypothesis about cause, not a grade' },
      insight: {
        ru: 'Красный маркер Кирилла раскрывается в причину «Пробел в теме „Законы Ньютона“» и одно ограниченное действие. Диагноз вероятностный: учитель подтверждает его на уроке.',
        en: 'Kirill’s red marker unfolds into a probable cause, a gap in Newton’s laws, and one bounded action. The diagnosis is a hypothesis the teacher confirms in class, not a verdict.',
      },
      tryThis: { hotspot: 'learner-2', label: { ru: 'Сравните жёлтый сигнал Богдана с красным', en: 'Compare Bogdan’s amber signal with the red one' } },
      questions: [
        { ru: 'Как система отличает пробел в теме от невнимательности?', en: 'How does the system tell a topic gap from carelessness?' },
        { ru: 'Какие данные стоят за красным сигналом Кирилла?', en: 'What evidence sits behind Kirill’s red signal?' },
      ],
    },
    parent: {
      headline: { ru: 'Ребёнка видят, а не сравнивают', en: 'Your child is seen, not ranked' },
      insight: {
        ru: 'На экране нет рейтинга: у каждого из двенадцати учеников свой сигнал. У Кирилла он ведёт не к замечанию, а к упражнению и мини-объяснению по законам Ньютона.',
        en: 'There is no leaderboard here: each of the twelve learners has their own signal. Kirill’s leads to an exercise and a short explanation on Newton’s laws, not to a reprimand.',
      },
      tryThis: { hotspot: 'learner-10', label: { ru: 'Нажмите на Максима — рост тоже сигнал', en: 'Press Maxim: growth is a signal too' } },
      questions: [
        { ru: 'Что увидит учитель о моём ребёнке на этом экране?', en: 'What would a teacher see about my child on this screen?' },
        { ru: 'Что происходит после красного сигнала?', en: 'What happens after a red signal appears?' },
      ],
    },
    investor: {
      headline: { ru: 'Единица продукта — решение учителя, а не отчёт', en: 'The product unit is a teacher decision, not a report' },
      insight: {
        ru: 'Экран класса заканчивается кнопкой «Применить решение»: сигнал, причина и действие связаны в один ход. Это логика продукта на синтетическом классе, а не витрина аналитики.',
        en: 'The class screen ends in an Apply button: signal, cause and action form one move. This is working product logic on a synthetic class, not an analytics showcase.',
      },
      tryThis: { hotspot: 'class-action', label: { ru: 'Нажмите «Применить решение» — увидите цепочку', en: 'Press Apply to see the causal chain' } },
      questions: [
        { ru: 'Чем это отличается от обычной панели успеваемости?', en: 'How is this different from a standard gradebook dashboard?' },
        { ru: 'Что из этого экрана уже работает в продукте?', en: 'Which parts of this screen work in the product today?' },
      ],
    },
  },

  motivation: {
    teacher: {
      headline: { ru: 'Это цепочка причин, а не лень', en: 'It is a chain of causes, not laziness' },
      insight: {
        ru: 'Выбрано звено 4 «Усилия без результата»: ученик решает, но не видит прогресса. Вмешательство простое — задание, где успех достижим уже сегодня, и видимый микро-прогресс.',
        en: 'Link 4 is selected: effort without visible result. The intervention is plain: give a task where success is reachable today and make the small progress visible.',
      },
      tryThis: { hotspot: 'cause-1', label: { ru: 'Откройте звено 2 — пробел в предпосылках', en: 'Open link 2, the prerequisite gap' } },
      questions: [
        { ru: 'С какого звена лучше начинать вмешательство?', en: 'Which link should an intervention start from?' },
        { ru: 'Что дать ученику, который старается, но не видит результата?', en: 'What do I give a learner who tries but sees no result?' },
      ],
    },
    expert: {
      headline: { ru: 'Семь звеньев: от пропуска концепции до потери мотивации', en: 'Seven links from a missed concept to lost motivation' },
      insight: {
        ru: 'Мотивация здесь — следствие обратной связи, а не черта характера: нет видимого прогресса → неочевиден результат → чувство тупика. Вмешательство бьёт по звену, а не по симптому.',
        en: 'Motivation is treated as a feedback effect, not a trait: no visible progress, unclear result, a sense of dead end. The intervention targets the link, not the symptom.',
      },
      tryThis: { hotspot: 'cause-6', label: { ru: 'Откройте звено 7 — куда ведёт цепочка', en: 'Open link 7 to see where the chain ends' } },
      questions: [
        { ru: 'Почему модель начинается с пропущенной концепции?', en: 'Why does the model start with a missed concept?' },
        { ru: 'Как система определяет, какое звено активно у ученика?', en: 'How does the system decide which link is active for a learner?' },
      ],
    },
    parent: {
      headline: { ru: 'Ребёнок не ленится — он застрял', en: 'Your child is not lazy, they are stuck' },
      insight: {
        ru: 'Ниндзя под куполом на звене 4 — это ощущение «сколько ни делай, не получается». Ответ системы не давление, а маленькая задача, с которой ребёнок сегодня точно справится.',
        en: 'The ninja under the dome at link 4 is the feeling of trying hard and getting nowhere. The system’s answer is not pressure but a small task the child can finish today.',
      },
      tryThis: { hotspot: 'cause-4', label: { ru: 'Откройте звено 5 — где падает уверенность', en: 'Open link 5, where confidence drops' } },
      questions: [
        { ru: 'Как понять дома, на каком звене сейчас ребёнок?', en: 'How can I tell at home which link my child is at?' },
        { ru: 'Не будет ли ребёнок чувствовать себя под наблюдением?', en: 'Will my child feel watched by this?' },
      ],
    },
    investor: {
      headline: { ru: 'Продукт разрывает замкнутый круг, а не продаёт контент', en: 'The product breaks a loop, it does not sell content' },
      insight: {
        ru: 'Каждое звено связано с конкретным вмешательством; подпись внизу — «Вмешательство разрывает замкнутый круг». Это педагогическая модель, зашитая в продукт, а не мотивационные бейджи.',
        en: 'Each link maps to a specific intervention; the footer reads that the intervention breaks the loop. This is a pedagogical model built into the product, not motivational badges.',
      },
      tryThis: { hotspot: 'motivation-intervention', label: { ru: 'Нажмите «Применить вмешательство»', en: 'Press Apply intervention' } },
      questions: [
        { ru: 'Почему такую модель мотивации сложно скопировать?', en: 'Why is this motivation model hard to copy?' },
        { ru: 'Где здесь заканчивается продукт и начинается методика?', en: 'Where does the product end and the method begin here?' },
      ],
    },
  },

  'teacher-goal': {
    teacher: {
      headline: { ru: 'Вы выбираете цель — маршрут перестраивается', en: 'You pick the goal, the route rebuilds itself' },
      insight: {
        ru: 'Выбрано «Понятие и смысл»: горизонт средний, диагностика углублённая, задания ситуационные, подсказки наводящие. Смените цель на «Применение на практике» — сдвинутся четыре оси.',
        en: 'Meaning and understanding is selected: medium horizon, deeper diagnosis, situational tasks, guiding questions. Switch to Apply in practice and all four axes move.',
      },
      tryThis: { hotspot: 'goal-1', label: { ru: 'Выберите «Применение на практике»', en: 'Pick Apply in practice' } },
      questions: [
        { ru: 'Какую цель выбрать перед контрольной?', en: 'Which goal should I pick before a test?' },
        { ru: 'Что меняется, если выбрать «Мастерство и перенос»?', en: 'What changes if I choose Mastery and transfer?' },
      ],
    },
    expert: {
      headline: { ru: 'Одна цель — четыре параметра маршрута', en: 'One goal, four route parameters' },
      insight: {
        ru: 'Цель учителя не выбирает задания напрямую: она задаёт горизонт, глубину диагностики, формат заданий и паттерн рекомендаций. Решает педагог, система лишь пересчитывает путь.',
        en: 'The teacher goal does not pick tasks directly: it sets the learning horizon, diagnostic depth, task format and hint pattern. The educator decides, the system recomputes the path.',
      },
      tryThis: { hotspot: 'axis-3-2', label: { ru: 'Обсудите «Открытые ориентиры» как паттерн подсказок', en: 'Discuss Open cues as the hint pattern' } },
      questions: [
        { ru: 'Почему глубина диагностики зависит от цели?', en: 'Why does diagnostic depth depend on the goal?' },
        { ru: 'Как связаны наводящие вопросы и уровень цели?', en: 'How are guiding questions tied to the goal level?' },
      ],
    },
    parent: {
      headline: { ru: 'Направление задаёт учитель, а не алгоритм', en: 'The teacher sets the direction, not an algorithm' },
      insight: {
        ru: 'Пять целей слева — от «Понятие и смысл» до «Мастерство и перенос» — это выбор человека. Внизу написано, почему выбран именно этот путь, и это можно прочитать вместе с ребёнком.',
        en: 'The five goals on the left, from Meaning to Mastery and transfer, are a human choice. The note at the bottom explains why this path was chosen, and you can read it with your child.',
      },
      tryThis: { hotspot: 'goal-3', label: { ru: 'Выберите «Исследование и творчество»', en: 'Pick Exploration and creativity' } },
      questions: [
        { ru: 'Кто решает, какую цель поставить моему ребёнку?', en: 'Who decides which goal is set for my child?' },
        { ru: 'Может ли цель меняться в течение года?', en: 'Can the goal change during the year?' },
      ],
    },
    investor: {
      headline: { ru: 'Учитель управляет системой, а не наоборот', en: 'The teacher steers the system, not the other way round' },
      insight: {
        ru: 'Механика продукта: одна педагогическая цель разворачивается в четыре оси маршрута. Схема «человек задаёт направление» отличает продукт от автопилотных рекомендателей.',
        en: 'The screen exposes the product mechanic: one pedagogical goal unfolds into four route axes. A human sets the direction, and that stance separates this from autopilot recommenders.',
      },
      tryThis: { hotspot: 'teacher-target', label: { ru: 'Нажмите на цель справа — откроется карта знаний', en: 'Press the target on the right to open the knowledge map' } },
      questions: [
        { ru: 'Что в этой механике уже реализовано в продукте?', en: 'How much of this mechanic is live in the product?' },
        { ru: 'Зачем учителю пять целей вместо одной кнопки?', en: 'Why five goals instead of one button?' },
      ],
    },
  },

  'knowledge-map': {
    teacher: {
      headline: { ru: 'Где именно рвётся квадратное уравнение', en: 'Where exactly quadratic equations break' },
      insight: {
        ru: 'В центре — квадратные уравнения. Ниже видно, что слабо: теорема Виета и неверное преобразование выражений подсвечены синим. «Подобрать задания» собирает практику именно под них.',
        en: 'Quadratic equations sit at the centre. Below, the weak spots are blue: Vieta’s theorem and wrong transformations. Select exercises builds practice for exactly those.',
      },
      tryThis: { hotspot: 'node-vieta', label: { ru: 'Откройте «Теорема Виета» — слабый узел', en: 'Open Vieta’s theorem, a weak node' } },
      questions: [
        { ru: 'Какие предпосылки проверить перед квадратными уравнениями?', en: 'Which prerequisites should I check before quadratics?' },
        { ru: 'Что дать ученику, у которого слабо с теоремой Виета?', en: 'What do I give a learner weak on Vieta’s theorem?' },
      ],
    },
    expert: {
      headline: { ru: 'Граф в пять слоёв, цвет — уровень освоения', en: 'A five layer graph where colour is mastery' },
      insight: {
        ru: 'Слои — концепции, предпосылки, методы решения, типичные ошибки, примеры. Освоено — лаймовый, слабо — синий, неизвестно — серый: «Игнорирование ОДЗ» ещё не проверялось, это не ноль.',
        en: 'Layers: concepts, prerequisites, methods, typical errors, examples. Lime means mastered, blue weak, grey unknown: Ignored domain has simply not been tested yet, it is not a zero.',
      },
      tryThis: { hotspot: 'layer-errors', label: { ru: 'Скройте слой «Типичные ошибки» — граф упростится', en: 'Hide the Typical errors layer to simplify the graph' } },
      questions: [
        { ru: 'Чем «неизвестно» отличается от «слабо» в модели освоения?', en: 'How does unknown differ from weak in the mastery model?' },
        { ru: 'Как ошибки связаны с методами в графе?', en: 'How are errors linked to methods in the graph?' },
      ],
    },
    parent: {
      headline: { ru: 'Тема — это карта, а не список параграфов', en: 'A topic is a map, not a list of chapters' },
      insight: {
        ru: 'Зелёные узлы ребёнок уже освоил, синие пока даются трудно, серые ещё не встречались. Карточка справа предлагает не наказание, а следующий шаг — закрепить формулу и потренироваться.',
        en: 'Green nodes are already mastered, blue ones still feel hard, grey ones have not come up yet. The card on the right offers a next step, practise the formula, not a penalty.',
      },
      tryThis: { hotspot: 'map-student', label: { ru: 'Включите «Режим ученика» — так это видит ребёнок', en: 'Switch to Student mode to see the child’s view' } },
      questions: [
        { ru: 'Что видит ребёнок на этой карте, а что только учитель?', en: 'What does my child see on this map and what is teacher only?' },
        { ru: 'Как ребёнку объясняют синие узлы?', en: 'How are the blue nodes explained to the child?' },
      ],
    },
    investor: {
      headline: { ru: 'Граф знаний — ядро продукта, а не иллюстрация', en: 'The knowledge graph is the product core, not a picture' },
      insight: {
        ru: 'Всё остальное — диагностика, маршрут, подбор заданий — читает этот граф. Слои и цвета освоения работают в продукте сегодня; здесь показана алгебра на синтетическом ученике.',
        en: 'Everything else, diagnosis, routing, task selection, reads from this graph. Layers and mastery colours work in the product today; this view shows algebra for a synthetic learner.',
      },
      tryThis: { hotspot: 'map-cta', label: { ru: 'Нажмите «Подобрать задания» — граф ведёт в маршрут', en: 'Press Select exercises: the graph feeds the route' } },
      questions: [
        { ru: 'Кто и как наполняет граф новыми предметами?', en: 'How does the graph get new subjects?' },
        { ru: 'Что мешает конкуренту повторить такой граф?', en: 'What makes this graph hard to replicate?' },
      ],
    },
  },

  'personal-route': {
    teacher: {
      headline: { ru: 'Почему у Алексея следующий шаг — квадратные уравнения', en: 'Why Alexey’s next step is quadratic equations' },
      insight: {
        ru: 'Внизу четыре причины: чаще ошибается в базовых типах, времени на неделе меньше, уверенность немного снизилась, мотивация высокая. Поэтому усиливаем базу и даём целевую практику.',
        en: 'Four reasons at the bottom: more errors in basic types, less time this week, confidence slightly down, motivation high. So the route strengthens the basics with targeted practice.',
      },
      tryThis: { hotspot: 'profile-time-1', label: { ru: 'Сдвиньте «Доступное время» — маршрут пересчитается', en: 'Move Available time and watch the route recompute' } },
      questions: [
        { ru: 'Что изменится, если у Алексея станет больше времени?', en: 'What changes if Alexey gets more time?' },
        { ru: 'Как использовать высокую мотивацию Алексея на уроке?', en: 'How do I use Alexey’s high motivation in class?' },
      ],
    },
    expert: {
      headline: { ru: 'Семь входов профиля, один пересчёт пути', en: 'Seven profile inputs, one route recomputation' },
      insight: {
        ru: 'Знания, цель, темп, уверенность, время, типичные ошибки, мотивация. Маршрут пересчитывается после каждого изменения, а блок «Почему шаг изменился» делает решение проверяемым.',
        en: 'Seven inputs: knowledge, goal, pace, confidence, time, errors, motivation. The route is recomputed after each change; the Why the step changed block keeps the decision inspectable.',
      },
      tryThis: { hotspot: 'profile-confidence-5', label: { ru: 'Поднимите «Уверенность» — обоснование шага сменится', en: 'Raise Confidence and the step rationale changes' } },
      questions: [
        { ru: 'Как взвешиваются семь параметров при выборе шага?', en: 'How are the seven inputs weighed when choosing a step?' },
        { ru: 'Что значит «типичные ошибки: по невнимательности / по теме»?', en: 'What does typical errors, careless versus topic, mean?' },
      ],
    },
    parent: {
      headline: { ru: 'Путь подстраивается под ребёнка, а не наоборот', en: 'The path adapts to the child, not the child to the path' },
      insight: {
        ru: 'У Алексея сейчас меньше времени и чуть меньше уверенности — и следующий шаг стал короче и проще: безошибочно решать базовые типы. Мотивация высокая, и её берегут, а не расходуют.',
        en: 'Alexey has less time and a bit less confidence right now, so the next step got shorter and simpler: solve the basic types cleanly. Motivation is high, and the route protects it.',
      },
      tryThis: { hotspot: 'route-compare-card', label: { ru: 'Сравните с Марией — у неё свой маршрут', en: 'Compare with Maria, who has her own route' } },
      questions: [
        { ru: 'Что будет, если ребёнок неделю не занимался?', en: 'What happens if my child skips a week?' },
        { ru: 'Может ли ребёнок сам менять свой профиль?', en: 'Can my child change their own profile?' },
      ],
    },
    investor: {
      headline: { ru: 'Маршрут объясним — это и есть отличие', en: 'An explainable route is the differentiator' },
      insight: {
        ru: 'Продукт не просто выдаёт шаг: под картой написано, из каких четырёх изменений он следует. Сравнение двух маршрутов показывает, что персонализация не сводится к порядку тем.',
        en: 'The product does not just emit a next step: beneath the map it lists the four changes it follows from. Comparing two routes shows personalisation is more than topic order.',
      },
      tryThis: { hotspot: 'route-compare', label: { ru: 'Нажмите «Сравнить с другим учеником»', en: 'Press Compare with another learner' } },
      questions: [
        { ru: 'Что здесь работает в продукте, а что показано на макете?', en: 'Which parts run in the product and which are shown as a mock?' },
        { ru: 'Чем объяснимый маршрут ценен для школы?', en: 'Why does an explainable route matter to a school?' },
      ],
    },
  },

  'learning-cycle': {
    teacher: {
      headline: { ru: 'Один урок не решает — решает цикл', en: 'One lesson does not fix it, the cycle does' },
      insight: {
        ru: 'Активен шаг 3 «Следующий доступный шаг»: достаточно сложный, доступный, своевременный. Шаг 7 «Сигнал учителю» возвращает данные вам — и вы решаете, менять ли маршрут.',
        en: 'Step 3 is active, the next available step: challenging, reachable, timely. Step 7, the teacher signal, hands the data back to you, and you decide whether to correct the route.',
      },
      tryThis: { hotspot: 'cycle-6', label: { ru: 'Откройте шаг 7 — сигнал учителю', en: 'Open step 7, the teacher signal' } },
      questions: [
        { ru: 'На каком шаге цикла нужно моё участие?', en: 'Which cycle step needs me personally?' },
        { ru: 'Что происходит, если ответ на шаге 5 неверный?', en: 'What happens when the answer at step 5 is wrong?' },
      ],
    },
    expert: {
      headline: { ru: 'Восемь этапов замыкают контур адаптации', en: 'Eight stages close the adaptation loop' },
      insight: {
        ru: 'Цель учителя → состояние → доступный шаг → короткий блок → анализ ответа → обновление карты → сигнал учителю → коррекция маршрута. Кольца слева — пройденные циклы.',
        en: 'Teacher goal, learner state, available step, short block, response analysis, map update, teacher signal, route correction. The rings on the left are the completed cycles.',
      },
      tryThis: { hotspot: 'cycle-5', label: { ru: 'Откройте шаг 6 — обновление карты знаний', en: 'Open step 6, the map update' } },
      questions: [
        { ru: 'Что именно обновляется в модели ученика на шаге 6?', en: 'What exactly updates in the learner model at step 6?' },
        { ru: 'Как шаг 3 балансирует сложность и доступность?', en: 'How does step 3 balance challenge and reachability?' },
      ],
    },
    parent: {
      headline: { ru: 'Маленькие порции и второй шанс', en: 'Small portions and a second chance' },
      insight: {
        ru: 'Шаг 4 — небольшая порция нового, шаг 5 — разбор, что получилось, а что нет. Ошибка не конец: она уточняет карту и подбирает следующий шаг — и так, пока не станет понятно.',
        en: 'Step 4 is a small portion of new material, step 5 looks at what worked and what did not. A mistake is not the end: it refines the map and shapes the next step, again and again.',
      },
      tryThis: { hotspot: 'cycle-3', label: { ru: 'Откройте шаг 4 — короткий учебный блок', en: 'Open step 4, the short learning block' } },
      questions: [
        { ru: 'Не устанет ли ребёнок от бесконечного цикла?', en: 'Will my child get tired of an endless cycle?' },
        { ru: 'Как я узнаю, что тема действительно понята?', en: 'How will I know the topic is really understood?' },
      ],
    },
    investor: {
      headline: { ru: 'Честная модель: цикл вместо обещания', en: 'An honest model: a cycle instead of a promise' },
      insight: {
        ru: 'Справа прямо написано: «Мы не обещаем, что одного урока будет достаточно». Продукт строится на повторяемом контуре с учителем внутри — это позиция, а не маркетинговый слоган.',
        en: 'The panel says it plainly: we do not promise one lesson is enough. The product is built as a repeatable loop with the teacher inside it, a design stance rather than a slogan.',
      },
      tryThis: { hotspot: 'cycle-next', label: { ru: 'Нажмите «Следующий цикл»', en: 'Press Next cycle' } },
      questions: [
        { ru: 'Какие шаги цикла автоматизированы, а какие за человеком?', en: 'Which cycle steps are automated and which stay human?' },
        { ru: 'Как история циклов используется дальше?', en: 'How is the cycle history used later?' },
      ],
    },
  },

  agency: {
    teacher: {
      headline: { ru: 'Награда за освоение, а не за галочку', en: 'Reward mastery, not the tick' },
      insight: {
        ru: 'Переключатель вверху — «награда за освоение». Внизу справа — что даёт режим «за действие»: пустые баллы, временное чувство, нет уверенности. Ученик видит, что решил 7 из 10 сам.',
        en: 'The toggle sits on reward for mastery. Bottom right lists what reward for action gives: empty points, a passing feeling, no confidence. The learner sees 7 of 10 solved unaided.',
      },
      tryThis: { hotspot: 'agency-toggle', label: { ru: 'Переключите режим награды — сравните панели', en: 'Flip the reward mode and compare the panels' } },
      questions: [
        { ru: 'Как объяснить классу разницу между баллами и освоением?', en: 'How do I explain points versus mastery to my class?' },
        { ru: 'Когда тьютор должен вмешиваться, а когда ждать?', en: 'When should the tutor step in and when wait?' },
      ],
    },
    expert: {
      headline: { ru: 'Агентность: цель, доказательство, шаг, выбор, поддержка', en: 'Agency: goal, evidence, step, choice, support' },
      insight: {
        ru: 'Пять элементов вдоль дорожки — ближайшая цель, видимое доказательство, последующий шаг, выбор маршрута, поддержка вовремя. Прогресс делится на понимание, применение и перенос.',
        en: 'Five elements along the path: near goal, visible evidence, next step, route choice, timely support. Progress is split into understanding, application and transfer, not one score.',
      },
      tryThis: { hotspot: 'agency-choice', label: { ru: 'Откройте «Выбор маршрута» — где даётся автономия', en: 'Open Route choice, where autonomy is given' } },
      questions: [
        { ru: 'Почему перенос в новую ситуацию оценивается отдельно?', en: 'Why is transfer to a new situation tracked separately?' },
        { ru: 'Что меняется в системе при режиме «награда за действие»?', en: 'What changes in the system under reward for action?' },
      ],
    },
    parent: {
      headline: { ru: 'Ребёнок чувствует: я могу', en: 'Your child feels: I can' },
      insight: {
        ru: 'Ребёнку показывают не оценку, а что он решил семь задач из десяти без подсказок и какой шаг следующий. Тьютор рядом замечает успех и предлагает попробовать по-новому.',
        en: 'The child is not shown a grade but that they solved seven of ten without hints and what comes next. The tutor nearby notices the win and suggests trying it a new way.',
      },
      tryThis: { hotspot: 'agency-support', label: { ru: 'Откройте «Поддержка вовремя» — подсказка, когда застрял', en: 'Open Timely support, a hint when stuck' } },
      questions: [
        { ru: 'Не подсказывает ли тьютор слишком много?', en: 'Does the tutor give away too much?' },
        { ru: 'Что ребёнок увидит, если у него не получилось?', en: 'What does my child see when something does not work out?' },
      ],
    },
    investor: {
      headline: { ru: 'Мотивация как механика продукта, а не геймификация', en: 'Motivation as product mechanics, not gamification' },
      insight: {
        ru: 'Переключатель «за действие / за освоение» — две модели подкрепления внутри одного продукта, и выбрана вторая: рост, который чувствуется. Бейджи здесь названы пустыми баллами.',
        en: 'The action versus mastery toggle is two reinforcement models inside one product, and it picks the second: growth you can feel. Badges are explicitly called empty points.',
      },
      tryThis: { hotspot: 'agency-mastery', label: { ru: 'Выберите «награда за освоение»', en: 'Select Reward for mastery' } },
      questions: [
        { ru: 'Чем эта модель отличается от очков и стриков?', en: 'How does this differ from points and streaks?' },
        { ru: 'Что из панели тьютора уже работает в продукте?', en: 'How much of the tutor panel is live in the product?' },
      ],
    },
  },

  'teacher-workspace': {
    teacher: {
      headline: { ru: 'Пять шагов от сигнала до нового маршрута', en: 'Five steps from a signal to a new route' },
      insight: {
        ru: 'Класс 7B Science, ученик Avery J., тема — фотосинтез. След ответов: связь понимает, рассуждать о растущих и ограничивающих факторах пока не может. Выбрано «Guided Exploration».',
        en: '7B Science, Avery J., photosynthesis. The trail shows Avery grasps the relationship but cannot yet reason with increasing and limiting factors. Guided Exploration is selected.',
      },
      tryThis: { hotspot: 'teacher-action-2', label: { ru: 'Выберите «Practice Set» — маршрут справа обновится', en: 'Pick Practice Set and watch the route update' } },
      questions: [
        { ru: 'Когда лучше выбрать мини-урок, а когда исследование?', en: 'When is a mini-lesson better than guided exploration?' },
        { ru: 'Какие свидетельства сильнее всего указывают на пробел?', en: 'Which evidence points most strongly to the gap?' },
      ],
    },
    expert: {
      headline: { ru: 'Свидетельства → вероятный пробел → действие', en: 'Evidence, probable gap, action' },
      insight: {
        ru: 'Пробел «Connecting Variables» выведен из трёх кластеров свидетельств: чтение графиков, причинное рассуждение, перенос. Ссылка «How we inferred this» показывает, как именно.',
        en: 'The gap, Connecting Variables, is inferred from three evidence clusters: graph interpretation, causal reasoning, transfer. How we inferred this shows the basis for that call.',
      },
      tryThis: { hotspot: 'workspace-gap', label: { ru: 'Откройте «Probable Gap» — как выведена причина', en: 'Open Probable Gap to see how the cause was inferred' } },
      questions: [
        { ru: 'Как три кластера свидетельств сводятся в один пробел?', en: 'How do three evidence clusters collapse into one gap?' },
        { ru: 'Что меняет Scaffold Level в предложенном действии?', en: 'What does Scaffold Level change in the proposed action?' },
      ],
    },
    parent: {
      headline: { ru: 'Учитель видит работу ребёнка, а не только ответ', en: 'The teacher sees the child’s work, not just the answer' },
      insight: {
        ru: 'В центре — рукописный ответ Avery о свете и растениях, подсказка и заметка «делает паузы после чтения графиков». Учитель выбирает исследование с поддержкой, а не «переделай».',
        en: 'In the centre: Avery’s handwritten answer about light and plants, the hint used, a note that Avery pauses over graphs. The teacher picks supported exploration, not a redo.',
      },
      tryThis: { hotspot: 'evidence-1', label: { ru: 'Откройте ответ ученика — что видит учитель', en: 'Open the learner’s response, what the teacher sees' } },
      questions: [
        { ru: 'Кто видит записи о моём ребёнке?', en: 'Who can see the notes about my child?' },
        { ru: 'Что означает «исследование с поддержкой» на практике?', en: 'What does guided exploration mean in practice?' },
      ],
    },
    investor: {
      headline: { ru: 'Рабочее место учителя — где создаётся ценность', en: 'The teacher workspace is where value is created' },
      insight: {
        ru: 'Рекомендация помечена «AI suggested based on evidence», но применяет её человек кнопкой Apply Action и правит параметры. Это разделение ролей — осознанная граница продукта.',
        en: 'The recommendation is labelled AI suggested based on evidence, yet a person applies it and the parameters stay adjustable. That division of roles is a deliberate product boundary.',
      },
      tryThis: { hotspot: 'workspace-route', label: { ru: 'Откройте «Updated Route» — результат решения', en: 'Open Updated Route, the outcome of the decision' } },
      questions: [
        { ru: 'Что в этом рабочем месте уже реализовано в продукте?', en: 'How much of this workspace is implemented today?' },
        { ru: 'Почему решение оставлено за учителем, а не автоматизировано?', en: 'Why keep the decision with the teacher instead of automating it?' },
      ],
    },
  },

  technology: {
    teacher: {
      headline: { ru: 'Семь механизмов, которые работают сегодня', en: 'Seven mechanisms that work today' },
      insight: {
        ru: 'Слева — то, что работает в продукте: знаниевая сеть, адаптивная диагностика, микроуроки, мастерство, личная карта, рекомендации учителю, AI-тьютор. Справа — прототипы вне продукта.',
        en: 'Left: the seven mechanisms that run in the product, from the knowledge network and adaptive diagnosis to teacher recommendations and the AI tutor. Right: prototypes outside it.',
      },
      tryThis: { hotspot: 'product-5', label: { ru: 'Откройте «Рекомендации учителю»', en: 'Open Teacher recommendations' } },
      questions: [
        { ru: 'Что из этого я увижу на своём уроке уже сейчас?', en: 'Which of these would I see in my own lessons now?' },
        { ru: 'Чем AI-тьютор отличается от обычного чата?', en: 'How is the AI tutor different from a plain chat?' },
      ],
    },
    expert: {
      headline: { ru: 'Продукт и исследование разделены явно', en: 'Product and research are separated explicitly' },
      insight: {
        ru: 'MagicBrain изучает, как принципы работы мозга могут помочь моделировать понимание. UWT ищет универсальные закономерности обучения. Оба помечены: «не входит в продукт сегодня».',
        en: 'MagicBrain explores how principles of brain function might help model understanding. UWT looks for general regularities of learning. Both are labelled not in the product today.',
      },
      tryThis: { hotspot: 'depth-method', label: { ru: 'Выберите глубину «Методист»', en: 'Select the Methodologist depth' } },
      questions: [
        { ru: 'Какие данные питают адаптивную диагностику?', en: 'What data feeds adaptive diagnosis?' },
        { ru: 'Как исследовательские прототипы отделены от продукта технически?', en: 'How are research prototypes separated from the product technically?' },
      ],
    },
    parent: {
      headline: { ru: 'Честно: что помогает сейчас, а что ещё изучают', en: 'Honest about what helps now and what is still studied' },
      insight: {
        ru: 'Зелёная рамка — то, с чем работает ребёнок: понятные объяснения под его контекст, закрепление через практику, план, который меняется вместе с ним. Правая часть — научные прототипы.',
        en: 'The green frame is what your child works with: short explanations in their context, practice that consolidates, a plan that changes with them. The right side is research only.',
      },
      tryThis: { hotspot: 'product-2', label: { ru: 'Откройте «Микроуроки» — короткие объяснения', en: 'Open Micro-lessons, the short explanations' } },
      questions: [
        { ru: 'Какие сведения о ребёнке нужны системе?', en: 'What information about my child does the system need?' },
        { ru: 'Могу ли я видеть, что именно объясняют ребёнку?', en: 'Can I see what is being explained to my child?' },
      ],
    },
    investor: {
      headline: { ru: 'Две территории: продукт и научные прототипы', en: 'Two territories: product and research prototypes' },
      insight: {
        ru: 'Семь механизмов слева описаны как работающие в продукте, две программы справа — как исследование с явной пометкой «не входит в продукт сегодня». Граница проведена до вопроса о ней.',
        en: 'Seven mechanisms on the left are working product; two programmes on the right are research, labelled not in the product today. The boundary is drawn before anyone asks.',
      },
      tryThis: { hotspot: 'research-magicbrain', label: { ru: 'Откройте MagicBrain — что именно исследуется', en: 'Open MagicBrain to see what is being researched' } },
      questions: [
        { ru: 'Что отличает архитектуру от обёртки над языковой моделью?', en: 'What separates this architecture from a wrapper over a language model?' },
        { ru: 'Как исследовательская линия связана с продуктовой?', en: 'How does the research line relate to the product line?' },
      ],
    },
  },

  finale: {
    teacher: {
      headline: { ru: 'Вы решаете, ученик движется, система связывает', en: 'You decide, the learner moves, the system connects' },
      insight: {
        ru: 'Верхний узел — ваш сигнал, нижний — путь ученика, между ними система. Спросите прямо здесь: «На чём сфокусировать ученика сейчас?» — ответ опирается на текущую сцену.',
        en: 'The top node is your signal, the bottom one the learner’s path, the system in between. Ask right here what the learner should focus on now; the answer draws on the current scene.',
      },
      tryThis: { hotspot: 'finale-prompt-1', label: { ru: 'Спросите: «На чём сфокусировать ученика сейчас?»', en: 'Ask: what should the learner focus on now?' } },
      questions: [
        { ru: 'С какой сцены начать показ коллегам?', en: 'Which scene should I show colleagues first?' },
        { ru: 'Как перенести это на мой предмет?', en: 'How would this carry over to my subject?' },
      ],
    },
    expert: {
      headline: { ru: 'Замкнутый контур: сигнал, адаптация, результат', en: 'A closed loop: signal, adaptation, result' },
      insight: {
        ru: 'Стрелки подписаны: «Адаптация и план» идёт от учителя к ученику через систему, «Результаты и понимание» — обратно. Четыре принципа внизу: безопасно, точно, сравнимо, гибко.',
        en: 'Adaptation and plan run from teacher to learner through the system; results and understanding run back. Four principles below: safe, precise, comparable, flexible.',
      },
      tryThis: { hotspot: 'finale-prompt-0', label: { ru: 'Спросите о различиях маршрутов А и В', en: 'Ask what differs between routes A and B' } },
      questions: [
        { ru: 'Где в контуре появляется генеративная модель?', en: 'Where in the loop does the generative model appear?' },
        { ru: 'Что означает «точно: фокус на синтетическом ученике»?', en: 'What does precise, focus on a synthetic learner, mean?' },
      ],
    },
    parent: {
      headline: { ru: 'Три роли, и ребёнок — одна из них', en: 'Three roles, and the child is one of them' },
      insight: {
        ru: 'Учитель даёт сигнал, ребёнок идёт своим путём, система связывает их. Демо построено на синтетическом ученике: данных реальных детей здесь нет, а спросить можно простым языком.',
        en: 'The teacher gives the signal, the child walks their own path, the system connects them. The demo uses a synthetic learner, no real children’s data, and you can ask in plain words.',
      },
      tryThis: { hotspot: 'finale-prompt-2', label: { ru: 'Попросите объяснить сцену проще', en: 'Ask for a simpler explanation of the scene' } },
      questions: [
        { ru: 'Что здесь самое важное для меня как родителя?', en: 'What matters most here for me as a parent?' },
        { ru: 'Как ребёнок будет чувствовать себя в этой системе?', en: 'How will my child feel inside this system?' },
      ],
    },
    investor: {
      headline: { ru: 'Один контур вместо набора функций', en: 'One loop instead of a feature list' },
      insight: {
        ru: 'Финал не перечисляет функции: он показывает продукт как контур между учителем, учеником и системой. Демо работает на синтетическом ученике, и о любой сцене можно спросить отсюда.',
        en: 'The finale lists no features: it shows the product as a loop between teacher, learner and system. The demo runs on a synthetic learner, and any scene can be questioned from here.',
      },
      tryThis: { hotspot: 'finale-system', label: { ru: 'Нажмите «Система» — вернётесь к технологии', en: 'Press System to return to the technology view' } },
      questions: [
        { ru: 'Что из показанного работает в продукте сегодня?', en: 'Which of what I saw runs in the product today?' },
        { ru: 'В чём главное отличие от других адаптивных платформ?', en: 'What is the main difference from other adaptive platforms?' },
      ],
    },
  },
};
